import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { isAdmin } from '../utils/role';

const emptyForm = {
  name: '', code: '', type: 'CUSTOMER', status: 'ACTIVE', industry: '',
  contactPerson: '', contactEmail: '', contactPhone: '', website: '', address: '', description: '',
};

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const admin = isAdmin();

  const [modal, setModal] = useState(null); // { id } editing or null for new
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/companies');
      setCompanies(data);
    } catch (e) {
      setError(e.response?.status
        ? `Failed to load companies (HTTP ${e.response.status}${e.response.data?.error ? ': ' + e.response.data.error : ''})`
        : 'Failed to load companies — is the backend running?');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setModal({ id: null }); setForm(emptyForm); setFormError(''); };
  const openEdit = (c) => {
    setModal({ id: c.id });
    setForm({
      name: c.name || '', code: c.code || '', type: c.type || 'CUSTOMER', status: c.status || 'ACTIVE',
      industry: c.industry || '', contactPerson: c.contactPerson || '', contactEmail: c.contactEmail || '',
      contactPhone: c.contactPhone || '', website: c.website || '', address: c.address || '', description: c.description || '',
    });
    setFormError('');
  };

  const save = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Company name is required'); return; }
    setSaving(true);
    try {
      if (modal.id) await api.put(`/companies/${modal.id}`, form);
      else await api.post('/companies', form);
      setModal(null); load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Companies</h1>
        {admin && <button className="btn-primary" onClick={openNew}>+ New Company</button>}
      </div>
      <p style={{ color: '#64748b', marginBottom: 20 }}>Client companies from the shared CLTS directory{admin ? '' : ' (read-only)'}</p>

      {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : companies.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>{error ? 'Could not load companies.' : 'No companies found.'}</div>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Code</th><th>Type</th><th>Contact</th><th>Status</th>{admin && <th></th>}</tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td>{c.code || '—'}</td>
                  <td>{c.type ? <span className="badge badge-info">{c.type}</span> : '—'}</td>
                  <td style={{ fontSize: 13 }}>{c.contactPerson || c.contactEmail || '—'}</td>
                  <td>{c.status || '—'}</td>
                  {admin && <td><button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(c)}>Edit</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }} onClick={() => setModal(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 6, color: '#0f172a', fontSize: 18 }}>{modal.id ? 'Edit Company' : 'New Company'}</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 18 }}>Shared across all CLTS modules (PM, DevOps, Support).</p>
            {formError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{formError}</div>}
            <Row>
              <Field label="Name *"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Code"><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. ACME" /></Field>
            </Row>
            <Row>
              <Field label="Type">
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="CUSTOMER">Customer</option>
                  <option value="VENDOR">Vendor</option>
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </Field>
            </Row>
            <Row>
              <Field label="Industry"><input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} /></Field>
              <Field label="Website"><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></Field>
            </Row>
            <Row>
              <Field label="Contact person"><input value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })} /></Field>
              <Field label="Contact email"><input value={form.contactEmail} onChange={e => setForm({ ...form, contactEmail: e.target.value })} /></Field>
            </Row>
            <Row>
              <Field label="Contact phone"><input value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} /></Field>
              <Field label="Address"><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
            </Row>
            <Field label="Description"><textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" onClick={save} disabled={saving} style={{ flex: 1 }}>{saving ? 'Saving…' : (modal.id ? 'Update' : 'Create')}</button>
              <button className="btn-ghost" onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4, color: '#374151' }}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}
